import React from 'react';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import OutboxOutlinedIcon from '@mui/icons-material/OutboxOutlined';
import AddIcon from '@mui/icons-material/Add';
import { INVOICE_NAV } from '../../utils/invoiceSchema';
import { iv } from './invoiceTheme';

const NavItem = ({ icon, label, active, collapsed, badge, onClick, disabled, nested }) => {
  const content = (
    <Box
      component="button"
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        px: collapsed ? { xs: 0.5, sm: 1.1 } : nested ? 2.2 : 1.4,
        py: 0.85,
        borderRadius: '8px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        bgcolor: active ? iv.primarySoft : 'transparent',
        color: active ? iv.primary : iv.text,
        fontWeight: active ? 700 : 550,
        fontSize: nested ? 12.5 : 13,
        mb: 0.25,
        '&:hover': disabled
          ? {}
          : {
              bgcolor: active ? iv.primarySoft : '#f1f5f9'
            }
      }}
    >
      <Box sx={{ display: 'grid', placeItems: 'center', width: 22, color: active ? iv.primary : iv.textMuted }}>
        {icon}
      </Box>
      {!collapsed ? (
        <>
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}
          </Box>
          {badge != null && badge > 0 ? (
            <Box
              sx={{
                minWidth: 20,
                px: 0.6,
                py: 0.1,
                borderRadius: '999px',
                bgcolor: active ? iv.primary : '#e2e8f0',
                color: active ? '#fff' : iv.textMuted,
                fontSize: 10.5,
                fontWeight: 800,
                textAlign: 'center',
                lineHeight: 1.5
              }}
            >
              {badge > 99 ? '99+' : badge}
            </Box>
          ) : null}
        </>
      ) : null}
    </Box>
  );

  if (collapsed) {
    return (
      <Tooltip title={disabled ? `${label} (yakında)` : label} placement="right">
        {content}
      </Tooltip>
    );
  }
  return content;
};

function InvoiceSidebar({
  collapsed,
  onToggleCollapsed,
  activeNav,
  onNavigate,
  onNewInvoice,
  creatingNew,
  badges = {},
  locked = {}
}) {
  return (
    <Box
      sx={{
        /* Telefonda daraltilmis serit 64px ile ekranin altida birini
           yiyordu; ikonlar 46px'e rahat siğiyor. */
        width: collapsed ? { xs: 46, sm: iv.sidebarCollapsedW } : iv.sidebarW,
        flexShrink: 0,
        height: '100%',
        bgcolor: iv.surface,
        borderRight: `1px solid ${iv.border}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.18s ease',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          height: 52,
          px: 1.2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          borderBottom: `1px solid ${iv.border}`
        }}
      >
        {!collapsed ? (
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: iv.textMuted, letterSpacing: '0.08em' }}>
            FATURALAR
          </Typography>
        ) : null}
        <IconButton size="small" onClick={onToggleCollapsed} aria-label="Menüyü daralt/genişlet">
          {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Yeni Fatura bir sayfa değil, bir eylem — menü kalemi yerine düğme. */}
      <Box sx={{ px: collapsed ? { xs: 0.4, sm: 0.75 } : 1, pt: 1.25, pb: 0.5 }}>
        {collapsed ? (
          <Tooltip title="Yeni Fatura" placement="right">
            <span>
              <IconButton
                size="small"
                onClick={() => onNewInvoice?.()}
                disabled={Boolean(creatingNew)}
                sx={{
                  width: '100%',
                  borderRadius: '8px',
                  bgcolor: iv.primary,
                  color: '#fff',
                  '&:hover': { bgcolor: iv.primaryHover },
                  '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#fff' }
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <Button
            fullWidth
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => onNewInvoice?.()}
            disabled={Boolean(creatingNew)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 13,
              boxShadow: 'none',
              bgcolor: iv.primary,
              '&:hover': { bgcolor: iv.primaryHover, boxShadow: 'none' }
            }}
          >
            {creatingNew ? 'Açılıyor…' : 'Yeni Fatura'}
          </Button>
        )}
      </Box>

      <Box sx={{ flex: 1, p: 1, pt: 0.5, overflowY: 'auto', overflowX: 'hidden' }}>
        <NavItem
          icon={<FactCheckOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Faturalar"
          active={activeNav === INVOICE_NAV.APPROVAL}
          collapsed={collapsed}
          badge={badges.approval}
          onClick={() => onNavigate(INVOICE_NAV.APPROVAL)}
        />
        <NavItem
          icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Faturalandırılacak"
          active={activeNav === INVOICE_NAV.TO_INVOICE}
          collapsed={collapsed}
          badge={badges.toInvoice}
          onClick={() => onNavigate(INVOICE_NAV.TO_INVOICE)}
        />
        <NavItem
          icon={<OutboxOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Giden Faturalar"
          active={activeNav === INVOICE_NAV.OUTBOUND}
          collapsed={collapsed}
          onClick={() => onNavigate(INVOICE_NAV.OUTBOUND)}
        />
        <NavItem
          icon={<InboxOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Gelen Faturalar"
          active={activeNav === INVOICE_NAV.INBOUND}
          collapsed={collapsed}
          badge={badges.inbound}
          disabled={locked.inbound !== false}
          onClick={() => onNavigate(INVOICE_NAV.INBOUND)}
        />
        <NavItem
          icon={<ReceiptOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Gider Pusulası"
          active={activeNav === INVOICE_NAV.EXPENSE_NOTE}
          collapsed={collapsed}
          badge={badges.expenseNote}
          onClick={() => onNavigate(INVOICE_NAV.EXPENSE_NOTE)}
        />
        <NavItem
          icon={<GppMaybeOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Risk Kontrolü"
          active={activeNav === INVOICE_NAV.AML}
          collapsed={collapsed}
          badge={badges.aml}
          onClick={() => onNavigate(INVOICE_NAV.AML)}
        />
        <NavItem
          icon={<AssessmentOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Raporlar"
          active={activeNav === INVOICE_NAV.REPORTS}
          collapsed={collapsed}
          disabled={locked.reports !== false}
          onClick={() => onNavigate(INVOICE_NAV.REPORTS)}
        />
        <NavItem
          icon={<SettingsOutlinedIcon sx={{ fontSize: 18 }} />}
          label="Ayarlar"
          active={activeNav === INVOICE_NAV.SETTINGS}
          collapsed={collapsed}
          onClick={() => onNavigate(INVOICE_NAV.SETTINGS)}
        />

      </Box>
    </Box>
  );
}

export default InvoiceSidebar;
